import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import NotFound from '@/pages/NotFound';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { siteInfo } from '@/mocks/site-info';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);
  if (!article) return <NotFound />;

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    datePublished: article.date,
    dateModified: article.date,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.51cg.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.51cg.com/article/${article.slug}`}
        ogType="article"
        articlePublishedTime={article.date}
        articleModifiedTime={article.date}
        schema={articleSchema}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '分类导航', to: '/categories' },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-10">
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {category && (
                <Link
                  to={`/categories?tab=${category.slug}`}
                  className="bg-primary-100 text-primary-700 text-xs px-3 py-1.5 rounded-md font-medium whitespace-nowrap hover:bg-primary-200 transition-colors cursor-pointer"
                >
                  {category.name}
                </Link>
              )}
              <span className="text-foreground-400 text-sm">{article.date}</span>
              <span className="text-foreground-400 text-sm">阅读 {article.readTime}</span>
              <span className="text-foreground-400 text-sm">作者：{article.author}</span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-foreground-500 text-base leading-relaxed">{article.excerpt}</p>
            <div className="flex items-center gap-2 mt-5 flex-wrap">
              {article.tags.map((tag) => (
                <span key={tag} className="text-foreground-400 text-xs bg-background-100 px-2.5 py-1 rounded-md whitespace-nowrap">
                  #{tag}
                </span>
              ))}
            </div>
          </header>

          <div
            className="prose prose-base max-w-none text-foreground-800 leading-relaxed
              prose-h2:text-xl prose-h2:md:text-2xl prose-h2:font-heading prose-h2:font-bold prose-h2:text-foreground-900 prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-lg prose-h3:font-heading prose-h3:font-semibold prose-h3:text-foreground-800 prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
              prose-ul:list-disc prose-ul:pl-6 prose-ul:text-foreground-700 prose-ul:mb-4
              prose-ol:list-decimal prose-ol:pl-6 prose-ol:text-foreground-700 prose-ol:mb-4
              prose-li:mb-1.5 prose-li:leading-relaxed
              prose-strong:text-foreground-900 prose-strong:font-semibold"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900">这篇文章对您有帮助吗？</h3>
                <p className="text-foreground-400 text-xs mt-1">欢迎分享给更多需要的朋友</p>
              </div>
              <div className="flex items-center gap-3">
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-background-100 text-foreground-600 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-thumb-up-line text-sm"></i> 有帮助
                </button>
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-background-100 text-foreground-600 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line text-sm"></i> 分享
                </button>
              </div>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <section className="mt-14">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">相关文章推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.slug} article={ra} />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}